import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Col, Row } from 'antd'
import { Formik } from 'formik'
import * as Yup from 'yup'

import getExpenseCategories, {
  ExpenseCategory,
} from 'core/application/adminDashboard/benefitDetails/design/expenseCategories'
import updateExpenseReimbursement from 'core/application/adminDashboard/benefitDetails/design/expenseReimbursement'

import Checkbox from 'common/components/dataEntry/components/Checkbox'
import FormikCheckbox from 'common/components/dataEntry/formik/FormikCheckboxField'
import CheckboxGroup from 'common/components/dataEntry/formik/FormikCheckboxGroupField'
import Radio from 'common/components/dataEntry/formik/FormikRadioField'
import Alert from 'common/components/feedback/Alert'
import notification from 'common/components/feedback/Notification'

import { commandRequest, queryRequest } from 'common/RequestUtils'

import { BaseComponentProps } from '../../../../common/productClassWizardModal/Common'

const formValidator = (translate: any) =>
  Yup.object({
    eobRequired: Yup.boolean()
      .required(translate('general.required'))
      .typeError(translate('general.required'))
      .nullable(),
    requiresPrescription: Yup.bool().typeError(translate('general.required')).nullable(),
    expenseCategories: Yup.array().nullable().notRequired(),
  })

export enum RequiresEobType {
  DoesNotRequireEob = 0,
  EobRequired = 1,
}

export interface ExpenseReimbursementValues {
  eobRequired: boolean | null
  requiresPrescription: boolean | null
  expenseCategories: string[]
  productClassId: string
  planPeriodId?: string
}

const ExpenseReimbursement: React.FC<BaseComponentProps<ExpenseReimbursementValues>> = (props) => {
  const { bindSubmitFormHandler } = props
  const [requiresEob, setRequiresEob] = useState<boolean | null>(props.state.eobRequired)
  const [categories, setExpenseCategories] = useState<ExpenseCategory[]>([])
  const [radioIsSwitched, setRadioIsSwitched] = useState<boolean>(false)
  const { t: translate } = useTranslation()
  const { planPeriod } = useParams<any>()
  const { productClassId } = props.state
  const updateExpenseCategories = async () => {
    return await queryRequest(() => getExpenseCategories())
  }

  useEffect(() => {
    updateExpenseCategories().then((r) => setExpenseCategories(r))
    props.setIsFormValid(formValidator(translate).isValidSync(props.state))
  }, [])

  const onSubmit = async (values: ExpenseReimbursementValues) => {
    let categories = values.expenseCategories
      ? values.expenseCategories.map((category) => {
          return +category
        })
      : []
    let reimbursementValues = {
      expenseCategories: categories,
      eobRequired: values.eobRequired ?? false,
      productClassId: productClassId,
      planPeriodId: planPeriod,
      requiresPrescription: values.requiresPrescription ?? false,
    }
    let response = await commandRequest(() => updateExpenseReimbursement(reimbursementValues))

    if (response.errors) {
      props.setIsSubmiting(false)
      console.log(response)
      notification.open({
        message: response.errors[0],
        type: 'error',
      })
    } else {
      if (!props.state.productClassId) {
        props.setProductClassId(response)
      }
      if (props.editing) {
        props.save()
      } else {
        props.goNext()
      }
    }
  }

  const handleRadioChange = (e: any, formik: any) => {
    let requiresEOB = e.target.value
    setRequiresEob(requiresEOB)
    if (requiresEOB) {
      formik.setFieldValue('expenseCategories', props.state.expenseCategories)
      setRadioIsSwitched(true)
    } else {
      formik.setFieldValue('expenseCategories', null)
    }
    formik.setFieldValue('eobRequired', requiresEOB)
  }

  const expenseCategoryComponent =
    categories.length > 0 &&
    categories.map((category) => (
      <Checkbox value={category.id.toString()} className="small" key={category.id}>
        {category.name}
      </Checkbox>
    ))

  const requiresEobComponent = requiresEob ? (
    <>
      <Row className="form__row">
        <Col span={22} className="p-8">
          <hr />
        </Col>
      </Row>
      <Row className="pt-8">
        <Col span={22} className="pl-8 pr-8">
          <Alert
            showIcon
            type="info"
            message={translate('admin-dashboard.benefit-details.class-design.require-eob.alert-exempt-header')}
            description={translate('admin-dashboard.benefit-details.class-design.require-eob.alert-exempt-desc')}
          />
        </Col>
      </Row>
      <Row className="form__row">
        <Col span={22}>
          <h5 className="expense_reimbursment_info pl-8 pr-8">
            {translate('admin-dashboard.benefit-details.class-design.require-eob.exempt-header')}
          </h5>
        </Col>
      </Row>

      <Row className="form__row pl-8 pr-8">
        <Col span={22}>
          <CheckboxGroup name="expenseCategories" defaultValue={radioIsSwitched ? [] : props.state.expenseCategories}>
            <Row>{expenseCategoryComponent}</Row>
          </CheckboxGroup>
          <FormikCheckbox
            defaultChecked={props.state.requiresPrescription === null ? undefined : props.state.requiresPrescription}
            className="small ml-8"
            name="requiresPrescription"
          >
            {translate('admin-dashboard.benefit-details.summary.prescription-label')}
          </FormikCheckbox>
        </Col>
      </Row>
    </>
  ) : null

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator(translate)}
      initialValues={props.state}
      onSubmit={onSubmit}
      validate={(values) => {
        const isValid: boolean = formValidator(translate).isValidSync(values)
        props.setHasDirtyData(isValid)
        props.setIsFormValid(isValid)
        return {}
      }}
    >
      {(formik) => {
        const { handleSubmit, submitForm } = formik
        bindSubmitFormHandler(submitForm)
        return (
          <form autoComplete="off" onSubmit={handleSubmit} className="expense_reimbursment">
            <Row>
              <Col span={22}>
                <h5 className="expense_reimbursment_info pl-8">
                  {translate('admin-dashboard.benefit-details.class-design.require-eob.header')}
                </h5>
              </Col>
            </Row>

            <Row className="pt-16">
              <Col span={22} className="pl-8 pr-8">
                <Alert
                  showIcon
                  type="info"
                  message={translate('admin-dashboard.benefit-details.class-design.require-eob.alert-header')}
                  description={
                    <>
                      <h6>
                        {translate('admin-dashboard.benefit-details.class-design.require-eob.alert-desc-paragraph-1')}
                      </h6>
                      <h6>
                        {translate('admin-dashboard.benefit-details.class-design.require-eob.alert-desc-paragraph-2')}
                      </h6>
                      <h6>
                        {translate('admin-dashboard.benefit-details.class-design.require-eob.alert-desc-paragraph-3')}
                      </h6>
                    </>
                  }
                  className="eob_info_alert"
                />
              </Col>
            </Row>

            <Row className="form__row">
              <Radio.Group
                name="eobRequired"
                defaultValue={props.state.eobRequired}
                onRadioChange={(e) => handleRadioChange(e, formik)}
              >
                <Row gutter={10}>
                  <Col xs={11}>
                    <Radio.Button value={false}>
                      <span className="line-height-normal">
                        {translate('admin-dashboard.benefit-details.class-design.require-eob.no-eob-desc')}
                      </span>
                    </Radio.Button>
                  </Col>
                  <Col xs={11}>
                    <Radio.Button value={true}>
                      <span className="line-height-normal">
                        {translate('admin-dashboard.benefit-details.class-design.require-eob.yes-eob-desc')}
                      </span>
                    </Radio.Button>
                  </Col>
                </Row>
                {requiresEobComponent}
              </Radio.Group>
            </Row>
          </form>
        )
      }}
    </Formik>
  )
}

export default ExpenseReimbursement
